import {
  addSortToBaseURL,
  URL_SORT_CONFIG,
} from "@/components/utils/global-state/sort-manager";
import { objectToQueryParams } from "@/flokit/utils/url/params";
import { useRouter } from "next/router";
import { EvaluationSettingsTabType } from "../modules/work-allocation/pages/evaluation-settings/utils/evaluation-settings-tabs";
import { AttorneyPortalTabType } from "@/modules/work-allocation/pages/attorney-evaluation/page";
import { AssociateProgramTabs } from "@/modules/work-allocation/pages/associate-program-view/types/tabs";

export const URLS = {
  govtContractor: {
    status: (org: string) => `/app/${org}/admin/settings/govt-contractor/`,
  },
  help: () => "https://help.florecruit.com/en/",
  /*────────────────────────────*\
     │           Admin            │
    \*────────────────────────────*/
  loginPageV1: (org: string) => `/app/${org}/admin/`,
  loginPageV2: (org: string) => `/app/${org}/login/`,
  forgotPasswordV2: (org: string) => `/app/${org}/forgot-password/`,
  forgotPasswordV2Success: (org: string, email: string) =>
    `/app/${org}/forgot-password/success/?email=${encodeURIComponent(email)}`,
  resetPasswordSuccess: (org: string) => `/app/${org}/reset-password/success/`,
  createAccountSuccess: (org: string) => `/app/${org}/create-account/success/`,
  members: (org: string) => `/app/${org}/admin/members/`,
  timezoneSettings: (org: string) => `/app/${org}/admin/settings/timezones`,
  passwordSettings: (org: string) => `/app/${org}/admin/settings/password/`,
  /*────────────────────────────*\
     │           Play App         │
    \*────────────────────────────*/
  eventList: (org: string) => `/app/${org}/admin/events`,
  event: (org: string, eventId: number) =>
    `/app/${org}/admin/events/${eventId}/`,
  recruitList: (org: string) => `/app/${org}/admin/recruits/`,
  liveDashboard: (org: string, eid: number) =>
    `/app/${org}/admin/events/virtual/${eid}/dashboard/`,
  /*────────────────────────────*\
     │           Members          │
    \*────────────────────────────*/
  membersUpload: (org: string) => `/app/${org}/admin/members/upload`,

  /*────────────────────────────*\
     │          Settings          │
    \*────────────────────────────*/
  universitySettingsPageEmployerDetails: (org: string) =>
    `/app/${org}/admin/employerDetailPage/`,
  universitySettingsPageChangePassword: (org: string) =>
    `/app/${org}/admin/settings/password/`,
  universitySettingsPageCustomizeAppearance: (org: string) =>
    `/app/${org}/admin/settings/appearance/`,
  universitySettingsPageEditCustomQuestions: (org: string) =>
    `/app/${org}/admin/settings/custom-questions/`,
  universitySettingsPageEditRecruitmentPolicy: (org: string) =>
    `/app/${org}/admin/settings/university-recruitment-policy/`,

  historicalData: (org: string) =>
    `/app/${org}/admin/settings/historical-data/`,
  internalFields: (org: string) =>
    `/app/${org}/admin/settings/internal-fields/`,
  candidatePhotoManagement: (org: string) =>
    `/app/${org}/admin/settings/candidate-photo-management/`,
  v1SettingsPage: (orgFriendlyName: string) =>
    `/app/${orgFriendlyName}/admin/settings/`,
  superAdmin: {
    v1: {
      allOrgs: (superAdminFriendlyName: string) =>
        `/app/${superAdminFriendlyName}/admin/super/allOrgs/`,
      createOrg: (superAdminFriendlyName: string) =>
        `/app/${superAdminFriendlyName}/admin/super/createOrg/`,
    },
    listManagement: {
      home: (superAdminFriendlyName: string) =>
        `/app/${superAdminFriendlyName}/admin/settings/super-admin/list-management/`,
      lawSchools: {
        home: (superAdminFriendlyName: string) =>
          `/app/${superAdminFriendlyName}/admin/settings/super-admin/list-management/law-schools`,
        add: (superAdminFriendlyName: string) =>
          `/app/${superAdminFriendlyName}/admin/settings/super-admin/list-management/law-schools/add`,
        rename: (superAdminFriendlyName: string) =>
          `/app/${superAdminFriendlyName}/admin/settings/super-admin/list-management/law-schools/rename`,
        merge: (superAdminFriendlyName: string) =>
          `/app/${superAdminFriendlyName}/admin/settings/super-admin/list-management/law-schools/merge`,
        assign: (superAdminFriendlyName: string) =>
          `/app/${superAdminFriendlyName}/admin/settings/super-admin/list-management/law-schools/assign`,
        domains: (superAdminFriendlyName: string) =>
          `/app/${superAdminFriendlyName}/admin/settings/super-admin/list-management/law-schools/domains`,
        orgs: (superAdminFriendlyName: string) =>
          `/app/${superAdminFriendlyName}/admin/settings/super-admin/list-management/law-schools/orgs`,
      },
      officeLocations: {
        home: (superAdminFriendlyName: string) =>
          `/app/${superAdminFriendlyName}/admin/settings/super-admin/list-management/office-locations`,
        add: (superAdminFriendlyName: string) =>
          `/app/${superAdminFriendlyName}/admin/settings/super-admin/list-management/office-locations/add`,
        rename: (superAdminFriendlyName: string) =>
          `/app/${superAdminFriendlyName}/admin/settings/super-admin/list-management/office-locations/rename`,
      },
      practiceAreas: {
        home: (superAdminFriendlyName: string) =>
          `/app/${superAdminFriendlyName}/admin/settings/super-admin/list-management/practice-areas`,
        add: (superAdminFriendlyName: string) =>
          `/app/${superAdminFriendlyName}/admin/settings/super-admin/list-management/practice-areas/add`,
        rename: (superAdminFriendlyName: string) =>
          `/app/${superAdminFriendlyName}/admin/settings/super-admin/list-management/practice-areas/rename`,
        edit: (superAdminFriendlyName: string) =>
          `/app/${superAdminFriendlyName}/admin/settings/super-admin/list-management/practice-areas/edit`,
      },
      practiceCategories: {
        home: (superAdminFriendlyName: string) =>
          `/app/${superAdminFriendlyName}/admin/settings/super-admin/list-management/practice-categories`,
        add: (superAdminFriendlyName: string) =>
          `/app/${superAdminFriendlyName}/admin/settings/super-admin/list-management/practice-categories/add`,
        rename: (superAdminFriendlyName: string) =>
          `/app/${superAdminFriendlyName}/admin/settings/super-admin/list-management/practice-categories/rename`,
      },
      universities: {
        home: (superAdminFriendlyName: string) =>
          `/app/${superAdminFriendlyName}/admin/settings/super-admin/list-management/universities`,
        add: (superAdminFriendlyName: string) =>
          `/app/${superAdminFriendlyName}/admin/settings/super-admin/list-management/universities/add`,
        rename: (superAdminFriendlyName: string) =>
          `/app/${superAdminFriendlyName}/admin/settings/super-admin/list-management/universities/rename`,
      },
      workplaces: {
        home: (superAdminFriendlyName: string) =>
          `/app/${superAdminFriendlyName}/admin/settings/super-admin/list-management/workplaces`,
        active: (superAdminFriendlyName: string) =>
          `/app/${superAdminFriendlyName}/admin/settings/super-admin/list-management/workplaces/active`,
        archived: (superAdminFriendlyName: string) =>
          `/app/${superAdminFriendlyName}/admin/settings/super-admin/list-management/workplaces/archived`,
      },
    },
    forward: {
      home: (orgFriendlyName: string) =>
        `/app/${orgFriendlyName}/admin/settings/super-admin/forward`,
      events: {
        home: (orgFriendlyName: string) =>
          `/app/${orgFriendlyName}/admin/settings/super-admin/forward/events`,
        unpublish: (orgFriendlyName: string) =>
          `/app/${orgFriendlyName}/admin/settings/super-admin/forward/events/unpublish`,
      },
      firms: {
        list: (orgFriendlyName: string) =>
          `/app/${orgFriendlyName}/admin/settings/super-admin/forward/firms`,
        practiceAreas: (orgFriendlyName: string, targetOrgId: string) =>
          `/app/${orgFriendlyName}/admin/settings/super-admin/forward/firms/${targetOrgId}/practice-areas`,
        offices: (orgFriendlyName: string, targetOrgId: string) =>
          `/app/${orgFriendlyName}/admin/settings/super-admin/forward/firms/${targetOrgId}/offices`,
        firmProfile: {
          highlights: (superAdminFriendlyName: string, firmId: string) =>
            `/app/${superAdminFriendlyName}/admin/settings/super-admin/forward/firms/${firmId}/firm-profile/highlights`,
          summerProgram: (superAdminFriendlyName: string, firmId: string) =>
            `/app/${superAdminFriendlyName}/admin/settings/super-admin/forward/firms/${firmId}/firm-profile/summer-program`,
          diversityAndInclusion: (
            superAdminFriendlyName: string,
            firmId: string,
          ) =>
            `/app/${superAdminFriendlyName}/admin/settings/super-admin/forward/firms/${firmId}/firm-profile/diversity-and-inclusion`,
          practiceAreas: (superAdminFriendlyName: string, firmId: string) =>
            `/app/${superAdminFriendlyName}/admin/settings/super-admin/forward/firms/${firmId}/firm-profile/practice-areas`,
          proBono: (superAdminFriendlyName: string, firmId: string) =>
            `/app/${superAdminFriendlyName}/admin/settings/super-admin/forward/firms/${firmId}/firm-profile/pro-bono`,
          eventsAndJobs: (superAdminFriendlyName: string, firmId: string) =>
            `/app/${superAdminFriendlyName}/admin/settings/super-admin/forward/firms/${firmId}/firm-profile/events-and-jobs`,
          custom: (superAdminFriendlyName: string, firmId: string) =>
            `/app/${superAdminFriendlyName}/admin/settings/super-admin/forward/firms/${firmId}/firm-profile/custom`,
          preview: (superAdminFriendlyName: string, firmId: string) =>
            `/app/${superAdminFriendlyName}/admin/settings/super-admin/forward/firms/${firmId}/firm-profile/preview`,
        },
        events: {
          home: (orgFriendlyName: string, targetOrgId: string) =>
            `/app/${orgFriendlyName}/admin/settings/super-admin/forward/firms/${targetOrgId}/events`,
          featured: (orgFriendlyName: string, targetOrgId: string) =>
            `/app/${orgFriendlyName}/admin/settings/super-admin/forward/firms/${targetOrgId}/events/featured`,
          unpublish: (orgFriendlyName: string, targetOrgId: string) =>
            `/app/${orgFriendlyName}/admin/settings/super-admin/forward/firms/${targetOrgId}/events/unpublish`,
        },
        privileges: (orgFriendlyName: string, targetOrgId: string) =>
          `/app/${orgFriendlyName}/admin/settings/super-admin/forward/firms/${targetOrgId}/privileges`,
      },
      jobs: {
        ats: {
          list: (orgFriendlyName: string) =>
            `/app/${orgFriendlyName}/admin/settings/super-admin/forward/jobs/ats`,
        },
        external: {
          list: (orgFriendlyName: string) =>
            `/app/${orgFriendlyName}/admin/settings/super-admin/forward/jobs/external`,
          create: (orgFriendlyName: string) =>
            `/app/${orgFriendlyName}/admin/settings/super-admin/forward/jobs/external/create`,
          edit: (orgFriendlyName: string, jobId: string) =>
            `/app/${orgFriendlyName}/admin/settings/super-admin/forward/jobs/external/${jobId}/edit`,
        },
        closeJobs: {
          list: (orgFriendlyName: string) =>
            `/app/${orgFriendlyName}/admin/settings/super-admin/forward/jobs/close-jobs`,
          confirm: (orgFriendlyName: string) =>
            `/app/${orgFriendlyName}/admin/settings/super-admin/forward/jobs/close-jobs/confirm`,
        },
      },
      jobImport: {
        home: (orgFriendlyName: string) =>
          `/app/${orgFriendlyName}/admin/settings/super-admin/forward/job-import`,
        batch: (orgFriendlyName: string, batchId: string) =>
          `/app/${orgFriendlyName}/admin/settings/super-admin/forward/job-import/${batchId}`,
        import: (orgFriendlyName: string, batchId: string, importId: string) =>
          `/app/${orgFriendlyName}/admin/settings/super-admin/forward/job-import/${batchId}/${importId}`,
      },
    },
    sendScheduledEmails: {
      list: (orgFriendlyName: string) =>
        `/app/${orgFriendlyName}/admin/settings/super-admin/send-scheduled-emails`,
      memberFeedbackReminderEmail: (orgFriendlyName: string) =>
        `/app/${orgFriendlyName}/admin/settings/super-admin/send-scheduled-emails/member-feedback-reminder-email`,
    },
  },

  Reporting: {
    conversionReports: {
      home: (org: string) => `/app/${org}/admin/reporting/reports`,
      report: (org: string, reportId: string) =>
        `/app/${org}/admin/reporting/reports/${reportId}`,
    },
  },

  /*────────────────────────────*\
     │            ATS             │
    \*────────────────────────────*/
  pipelineTemplateSettings: (org: string) =>
    `/app/${org}/admin/ats/pipeline-templates/overview`,
  pipelineTemplate: (org: string, templateId: number) =>
    `/app/${org}/admin/ats/pipeline-templates/${templateId}`,
  adminSettings: (org: string) => `/app/${org}/admin/settings`,
  editCorePipelineStatuses: (org: string) =>
    `/app/${org}/admin/settings/core-pipeline-statuses`,
  applicationQuestionSettings: (org: string) =>
    `/app/${org}/admin/settings/application-questions`,
  departmentAndPositionSettings: (org: string) =>
    `/app/${org}/admin/settings/departments-positions/overview`,
  eeoQuestionSettings: (org: string) =>
    `/app/${org}/admin/settings/eeo-questions/settings`,
  createJob: (org: string) => `/app/${org}/admin/ats/jobs/create-job`,
  editJob: (org: string, jobId: number) =>
    `/app/${org}/admin/ats/jobs/${jobId}/edit-job`,
  duplicateJob: (org: string, jobId: number) =>
    `/app/${org}/admin/ats/jobs/${jobId}/duplicate-job`,
  jobsOverview: (org: string) => `/app/${org}/admin/ats/jobs/overview`,
  jobDetails: (org: string, jobId: number) =>
    `/app/${org}/admin/ats/jobs/${jobId}/details`,
  qualifyingQuestionSettings: (org: string) =>
    `/app/${org}/admin/settings/qqs/`,
  customizeAppearanceSettings: (org: string) =>
    `/app/${org}/admin/settings/appearance/`,
  officeLocationSettings: (org: string) =>
    `/app/${org}/admin/settings/office-locations/`,
  stats: (org: string) => `/app/${org}/admin/home`,
  exportCommentsData: (org: string) => `/app/${org}/admin/home/comments.csv`,
  oldCandidateList: (org: string) => `/app/${org}/admin/ats/candidates`,
  reportingCandidateList: (org: string) =>
    `/app/${org}/admin/reporting/candidates`,
  candidateProfile: (org: string, candidateId: string) =>
    `/app/${org}/admin/recruits/${candidateId}/`,
  exportJobApplications: (jobId: number) => `/api/jobs/${jobId}/export`,

  selfIdentificationSettings: (org: string) =>
    `/app/${org}/admin/settings/self-identification/`,
  selfApplyJobsList: (org: string, applicantSignInAutoPrompt?: boolean) =>
    `/app/${org}/jobs/${
      applicantSignInAutoPrompt ? "?applicantSignInAutoPrompt=true" : ""
    }`,
  selfApplyJobApplication: (org: string, jobExtension: string) =>
    `/app/${org}/jobs/${jobExtension}/apply/`,
  selfApplyJobApplicationSuccess: (org: string, jobExtension: string) =>
    `/app/${org}/jobs/${jobExtension}/apply/success/`,

  agencyProfilePage: (org: string, agencyId: string) =>
    `/app/${org}/admin/settings/agencies/${agencyId}/`,
  agencyOverviewPage: (org: string) => `/app/${org}/admin/settings/agencies/`,

  serendipitousStudentRegistrationSuccess: (args: {
    org: string;
    eid: string;
    studentExtension: string;
  }) => {
    const { org, eid, studentExtension } = args;
    return `/app/${org}/registration/serendipitous/${eid}/student/register/${studentExtension}/success`;
  },
  agencySelfApplyJobsList: (org: string, autoPrompt?: boolean) =>
    `/app/${org}/agency/jobs/${autoPrompt ? "?autoSignInPrompt=true" : ""}`,
  agencySelfApplyJobApplication: (org: string, jobExtension: string) =>
    `/app/${org}/agency/jobs/${jobExtension}/apply/`,
  agencySelfApplyJobApplicationSuccess: (org: string, jobExtension: string) =>
    `/app/${org}/agency/jobs/${jobExtension}/apply/success/`,
  employer: {
    events: {
      universityNetworkingEmployer: (org: string, eventId: string) =>
        `/app/${org}/admin/events/university-networking-employer/${eventId}`,
      editUniversityNetworkingEmployer: (org: string, eventId: string) =>
        `/app/${org}/admin/events/university-networking-employer/${eventId}/edit`,
      editFeedbackUniversityNetworkingEmployer: (
        org: string,
        eventId: string,
      ) =>
        `/app/${org}/admin/events/university-networking-employer/${eventId}/edit/feedback`,
    },
  },

  /*────────────────────────────*\
      │         Event             │
    \*────────────────────────────*/
  eventRegistrationEntryPoint: (orgFriendlyName: string, eventId: number) =>
    `/app/${orgFriendlyName}/registration/events/${eventId}/register`,
  /*────────────────────────────*\
      │    Serendipitous           │
    \*────────────────────────────*/
  serendipitous: {
    candidateProfileJobAppDocument: (
      jobAppId: number,
      jobAppDocumentId: number,
    ) => `/api/v2/job-app/jobAppDocuments/${jobAppId}/${jobAppDocumentId}`,
    deleteEvent: (org: string, eventId: string) =>
      `/app/${org}/admin/events/${eventId}/delete/`,
    edit: (org: string, eventId: string) =>
      `/app/${org}/admin/events/serendipitous/${eventId}/edit/event-details`,
    editEmails: (org: string, eid: string) =>
      `/app/${org}/admin/events/serendipitous/${eid}/edit/emails`,
    editEventRooms: (org: string, eid: string) =>
      `/app/${org}/admin/events/serendipitous/${eid}/edit/event-rooms`,
    eventDetails: (org: string, eventId: string) =>
      `/app/${org}/admin/events/serendipitous/${eventId}`,
    lobby: (
      org: string,
      eid: string,
      attendeeType: string,
      extension: string,
    ) => `/app/${org}/events/serendipitous/${eid}/${attendeeType}/${extension}`,
    oldStudentRegistration: (org: string, eid: string) =>
      `/app/${org}/registration/serendipitous/${eid}/student/register/`,
    videoChat: (
      org: string,
      eid: string,
      attendeeType: string,
      extension: string,
      roomId: string,
      dailyname: string,
    ) =>
      `/app/${org}/events/serendipitous/${eid}/${attendeeType}/${extension}/video/${roomId}/${dailyname}`,
    webinar: (
      org: string,
      eid: string,
      attendeeType: string,
      extension: string,
      webinarId: string,
      dailyname: string,
    ) =>
      `/app/${org}/events/serendipitous/${eid}/${attendeeType}/${extension}/video/webinar/${webinarId}/${dailyname}`,
  },

  /*──────────────────────────────────────*\
     │         Interview Scheduling         │
    \*──────────────────────────────────────*/
  interviewSchedulingEmployerRegistration: (
    orgFriendlyName: string,
    eventId: number,
    extension: string,
  ) =>
    `/app/${orgFriendlyName}/registration/interview-scheduling/${eventId}/employer/register/${extension}`,

  /*────────────────────────────*\
     │         Preselect          │
    \*────────────────────────────*/
  preselect: {
    university: {
      eventView: (orgFriendlyName: string, universityEventId: number) =>
        `/app/${orgFriendlyName}/admin/events/preselect/${universityEventId}`,
      editAssignments: (orgFriendlyName: string, universityEventId: number) =>
        `/app/${orgFriendlyName}/admin/events/preselect/${universityEventId}/edit-assignments`,
      editSchedule: (orgFriendlyName: string, universityEventId: number) =>
        `/app/${orgFriendlyName}/admin/events/preselect/${universityEventId}/edit-schedule`,
      scheduleView: (
        universityOrgFriendlyName: string,
        universityEventId: number,
        employerEventId: number,
        scheduleId: number,
      ) =>
        `/app/${universityOrgFriendlyName}/admin/events/preselect/${universityEventId}/${employerEventId}/schedules/${scheduleId}/`,
      scheduleListView: (
        universityOrgFriendlyName: string,
        universityEventId: number,
        employerEventId: number,
      ) =>
        `/app/${universityOrgFriendlyName}/admin/events/preselect/${universityEventId}/${employerEventId}/schedules/`,
      studentJobsView: (
        orgFriendlyName: string,
        universityEventId: number,
        studentExtension: string,
      ) =>
        `/app/${orgFriendlyName}/admin/events/preselect/${universityEventId}/student/${studentExtension}/jobs/`,
      applicationView: (
        orgFriendlyName: string,
        universityEventId: number,
        studentExtension: string,
        scheduleId: number,
      ) =>
        `/app/${orgFriendlyName}/admin/events/preselect/${universityEventId}/student/${studentExtension}/jobs/${scheduleId}`,
      applicationListView: (
        universityOrgFriendlyName: string,
        universityEventId: number,
        employerEventId: number,
        scheduleId: number,
      ) =>
        `/app/${universityOrgFriendlyName}/admin/events/preselect/${universityEventId}/${employerEventId}/student-applications/${scheduleId}`,
      eventCreation: {
        eventDetails: (orgFriendlyName: string, universityEventId?: number) =>
          universityEventId === undefined
            ? `/app/${orgFriendlyName}/admin/events/preselect/create-event`
            : `/app/${orgFriendlyName}/admin/events/preselect/${universityEventId}/event-details`,
        schedule: (orgFriendlyName: string, universityEventId: number) =>
          `/app/${orgFriendlyName}/admin/events/preselect/${universityEventId}/event-schedule`,
        eventTimeline: (orgFriendlyName: string, universityEventId: number) =>
          `/app/${orgFriendlyName}/admin/events/preselect/${universityEventId}/event-timeline`,
        employerSettings: (
          orgFriendlyName: string,
          universityEventId: number,
        ) =>
          `/app/${orgFriendlyName}/admin/events/preselect/${universityEventId}/employer-settings`,
        automatedEmails: (orgFriendlyName: string, universityEventId: number) =>
          `/app/${orgFriendlyName}/admin/events/preselect/${universityEventId}/automated-emails`,
      },
    },
    employer: {
      eventView: (orgFriendlyName: string, employerEventId: number) =>
        `/app/${orgFriendlyName}/admin/events/preselect-employer/${employerEventId}`,

      editEmails: (orgFriendlyName: string, employerEventId: string) =>
        `/app/${orgFriendlyName}/admin/events/preselect-employer/${employerEventId}/edit-emails`,
      editFeedback: (orgFriendlyName: string, employerEventId: string) =>
        `/app/${orgFriendlyName}/admin/events/preselect-employer/${employerEventId}/edit-feedback`,
      scheduleView: (
        orgFriendlyName: string,
        employerEventId: number,
        scheduleId: number,
      ) =>
        `/app/${orgFriendlyName}/admin/events/preselect-employer/${employerEventId}/${scheduleId}`,
      applicationView: (
        orgFriendlyName: string,
        employerEventId: number,
        scheduleId: number,
      ) =>
        `/app/${orgFriendlyName}/admin/events/preselect-employer/${employerEventId}/${scheduleId}/student-applications`,
    },
    student: {
      informationForm: (orgFriendlyName: string, eid: number) =>
        `/app/${orgFriendlyName}/registration/preselect/${eid}/student/register/information`,
      eventView: (
        orgFriendlyName: string,
        universityEventId: number,
        studentExtension: string,
      ) =>
        `/app/${orgFriendlyName}/events/preselect/${universityEventId}/student/${studentExtension}`,
      scheduleView: (
        orgFriendlyName: string,
        universityEventId: number,
        studentExtension: string,
      ) =>
        `/schedules/${orgFriendlyName}/${universityEventId}/candidate/${studentExtension}`,
      conflictsView: (
        orgFriendlyName: string,
        universityEventId: number,
        studentExtension: string,
      ) =>
        `/app/${orgFriendlyName}/events/preselect/${universityEventId}/student/${studentExtension}/conflicts`,
      jobView: (
        orgFriendlyName: string,
        universityEventId: number,
        studentExtension: string,
        scheduleId: number,
      ) =>
        `/app/${orgFriendlyName}/events/preselect/${universityEventId}/student/${studentExtension}/${scheduleId}`,
    },
  },
  /*────────────────────────────*\
     │         Lottery          │
    \*────────────────────────────*/
  lottery: {
    student: {
      viewBids: (
        orgFriendlyName: string,
        eventId: number,
        studentExtension: string,
      ) => {
        return `/app/${orgFriendlyName}/events/lottery/${eventId}/student/${studentExtension}`;
      },
      editBids: (
        orgFriendlyName: string,
        eventId: number,
        studentExtension: string,
      ) => {
        return `/app/${orgFriendlyName}/events/lottery/${eventId}/student/${studentExtension}/edit`;
      },
      scheduleView: (
        orgFriendlyName: string,
        universityEventId: number,
        studentExtension: string,
      ) =>
        `/schedules/${orgFriendlyName}/${universityEventId}/candidate/${studentExtension}`,
      documentUpload: (
        orgFriendlyName: string,
        eventId: number,
        studentExtension: string,
      ) => {
        return `/app/${orgFriendlyName}/events/lottery/${eventId}/student/${studentExtension}/document-upload`;
      },
      selfScheduleDocumentUpload: (
        orgFriendlyName: string,
        eventId: number,
        studentExtension: string,
      ) => {
        return `/app/${orgFriendlyName}/events/lottery/${eventId}/student/${studentExtension}/self-schedule/document-upload`;
      },
      conflictsView: (
        orgFriendlyName: string,
        universityEventId: number,
        studentExtension: string,
      ) =>
        `/app/${orgFriendlyName}/events/lottery/${universityEventId}/student/${studentExtension}/conflicts`,
    },
    university: {
      student: {
        viewBids: (
          orgFriendlyName: string,
          eventId: number,
          studentExtension: string,
        ) => {
          return `/app/${orgFriendlyName}/admin/events/lottery/${eventId}/student/${studentExtension}`;
        },
        editBids: (
          orgFriendlyName: string,
          eventId: number,
          studentExtension: string,
        ) => {
          return `/app/${orgFriendlyName}/admin/events/lottery/${eventId}/student/${studentExtension}/edit`;
        },
        documentUpload: (
          orgFriendlyName: string,
          eventId: number,
          studentExtension: string,
        ) => {
          return `/app/${orgFriendlyName}/admin/events/lottery/${eventId}/student/${studentExtension}/document-upload`;
        },
        conflictsView: (
          orgFriendlyName: string,
          universityEventId: number,
          studentExtension: string,
        ) =>
          `/app/${orgFriendlyName}/admin/events/lottery/${universityEventId}/student/${studentExtension}/conflicts`,
        selfSchedule: (
          orgFriendlyName: string,
          univeristyEventId: number,
          studentExtension: string,
        ) =>
          `/app/${orgFriendlyName}/admin/events/lottery/${univeristyEventId}/student/${studentExtension}/self-schedule`,
      },
      eventView: (orgFriendlyName: string, universityEventId: number) =>
        `/app/${orgFriendlyName}/admin/events/lottery/${universityEventId}`,
      editAssignments: (orgFriendlyName: string, universityEventId: number) =>
        `/app/${orgFriendlyName}/admin/events/lottery/${universityEventId}/edit-assignments`,
      editSchedule: (orgFriendlyName: string, universityEventId: number) =>
        `/app/${orgFriendlyName}/admin/events/lottery/${universityEventId}/edit-schedule`,
      scheduleView: (
        universityOrgFriendlyName: string,
        universityEventId: number,
        employerEventId: number,
        scheduleId: number,
      ) =>
        `/app/${universityOrgFriendlyName}/admin/events/lottery/${universityEventId}/${employerEventId}/schedules/${scheduleId}/`,
      scheduleListView: (
        universityOrgFriendlyName: string,
        universityEventId: number,
        employerEventId: number,
      ) =>
        `/app/${universityOrgFriendlyName}/admin/events/lottery/${universityEventId}/${employerEventId}/schedules/`,
      studentJobsView: (
        orgFriendlyName: string,
        universityEventId: number,
        studentExtension: string,
      ) =>
        `/app/${orgFriendlyName}/admin/events/lottery/${universityEventId}/student/${studentExtension}/jobs/`,
      applicationView: (
        orgFriendlyName: string,
        universityEventId: number,
        studentExtension: string,
        scheduleId: number,
      ) =>
        `/app/${orgFriendlyName}/admin/events/lottery/${universityEventId}/student/${studentExtension}/jobs/${scheduleId}`,
      applicationListView: (
        universityOrgFriendlyName: string,
        universityEventId: number,
        employerEventId: number,
        scheduleId: number,
      ) =>
        `/app/${universityOrgFriendlyName}/admin/events/lottery/${universityEventId}/${employerEventId}/student-applications/${scheduleId}`,
      eventCreation: {
        eventDetails: (orgFriendlyName: string, universityEventId?: number) =>
          universityEventId === undefined
            ? `/app/${orgFriendlyName}/admin/events/lottery/create-event`
            : `/app/${orgFriendlyName}/admin/events/lottery/${universityEventId}/event-details`,
        schedule: (orgFriendlyName: string, universityEventId: number) =>
          `/app/${orgFriendlyName}/admin/events/lottery/${universityEventId}/event-schedule`,
        eventTimeline: (orgFriendlyName: string, universityEventId: number) =>
          `/app/${orgFriendlyName}/admin/events/lottery/${universityEventId}/event-timeline`,
        employerSettings: (
          orgFriendlyName: string,
          universityEventId: number,
        ) =>
          `/app/${orgFriendlyName}/admin/events/lottery/${universityEventId}/employer-settings`,
        automatedEmails: (orgFriendlyName: string, universityEventId: number) =>
          `/app/${orgFriendlyName}/admin/events/lottery/${universityEventId}/automated-emails`,
      },
    },
    employer: {
      eventView: (orgFriendlyName: string, employerEventId: number) =>
        `/app/${orgFriendlyName}/admin/events/lottery-employer/${employerEventId}`,

      editEmails: (orgFriendlyName: string, employerEventId: string) =>
        `/app/${orgFriendlyName}/admin/events/lottery-employer/${employerEventId}/edit-emails`,
      editFeedback: (orgFriendlyName: string, employerEventId: string) =>
        `/app/${orgFriendlyName}/admin/events/lottery-employer/${employerEventId}/edit-feedback`,
      scheduleView: (
        orgFriendlyName: string,
        employerEventId: number,
        scheduleId: number,
      ) =>
        `/app/${orgFriendlyName}/admin/events/lottery-employer/${employerEventId}/${scheduleId}`,
      applicationView: (
        orgFriendlyName: string,
        employerEventId: number,
        scheduleId: number,
      ) =>
        `/app/${orgFriendlyName}/admin/events/lottery-employer/${employerEventId}/${scheduleId}/lottery-matches`,
    },
  },

  /*────────────────────────────*\
     │       Work Allocation      │
    \*────────────────────────────*/
  workAllocation: {
    evaluations: {
      view: (orgFriendlyName: string, tab?: EvaluationSettingsTabType) =>
        `/app/${orgFriendlyName}/admin/settings/evaluations${
          tab ? `?tab=${tab}` : "/"
        }`,
      feedbackList: (
        orgFriendlyName: string,
        programId: string,
        tab?: AttorneyPortalTabType,
      ) => {
        return `/app/${orgFriendlyName}/work-allocation/programs/${programId}/feedback${
          tab ? `?tab=${tab}` : ""
        }`;
      },
      submitProjectFeedback: (
        orgFriendlyName: string,
        programId: string,
        matterId: string,
        candidateId: string,
      ) =>
        `/app/${orgFriendlyName}/work-allocation/programs/${programId}/matters/${matterId}/associate/${candidateId}/feedback`,
      submitProgramFeedback: (
        orgFriendlyName: string,
        programId: string,
        evaluationId: string,
        associateRecruitId: string,
      ) =>
        `/app/${orgFriendlyName}/work-allocation/programs/${programId}/evaluations/${evaluationId}/associate/${associateRecruitId}`,
    },
    associate: {
      program: (
        orgFriendlyName: string,
        programId: string,
        tab: AssociateProgramTabs,
      ) => {
        return tab
          ? `/app/${orgFriendlyName}/work-allocation/associate/programs/${programId}?tab=${tab.toString()}`
          : `/app/${orgFriendlyName}/work-allocation/associate/programs/${programId}`;
      },
      evaluation: {
        editFeedback: (
          orgFriendlyName: string,
          programId: string,
          evaluationId: string,
        ) => {
          return `/app/${orgFriendlyName}/work-allocation/associate/programs/${programId}/evaluations/${evaluationId}/feedback`;
        },
      },
      matter: (
        orgFriendlyName: string,
        programId: string,
        matterId: string,
      ) => {
        return `/app/${orgFriendlyName}/work-allocation/associate/programs/${programId}/matters/${matterId}`;
      },
      createMatter: (orgFriendlyName: string, programId: string) =>
        `/app/${orgFriendlyName}/work-allocation/associate/programs/${programId}/associate-create-matter`,
      editMatter: (
        orgFriendlyName: string,
        programId: string,
        matterId: string,
      ) =>
        `/app/${orgFriendlyName}/work-allocation/associate/programs/${programId}/associate-create-matter?matterId=${matterId}`,
    },
    matters: {
      create: (orgFriendlyName: string, programId: string) =>
        `/app/${orgFriendlyName}/admin/work-allocation/programs/${programId}/create-matter`,
      requestMatter: (orgFriendlyName: string, programId: string) =>
        `/app/${orgFriendlyName}/work-allocation/programs/${programId}/request-matter`,
      requestMatterSuccess: (orgFriendlyName: string, programId: string) =>
        `/app/${orgFriendlyName}/work-allocation/programs/${programId}/request-matter-success`,
      view: (orgFriendlyName: string, programId: string, matterId: string) =>
        `/app/${orgFriendlyName}/admin/work-allocation/programs/${programId}/matters/${matterId}`,
      edit: (orgFriendlyName: string, programId: string, matterId: string) =>
        `/app/${orgFriendlyName}/admin/work-allocation/programs/${programId}/create-matter?matterId=${matterId}`,
    },
    programs: {
      list: (orgFriendlyName: string) =>
        `/app/${orgFriendlyName}/admin/work-allocation/programs`,
      view: (orgFriendlyName: string, programId: string) =>
        `/app/${orgFriendlyName}/admin/work-allocation/programs/${programId}`,
      viewMatters: (orgFriendlyName: string, programId: string) =>
        `/app/${orgFriendlyName}/admin/work-allocation/programs/${programId}?tab=matters`,
      create: {
        programDetails: (orgFriendlyName: string) =>
          `/app/${orgFriendlyName}/admin/work-allocation/programs/create`,
        programEvaluation: (orgFriendlyName: string, programId: string) =>
          `/app/${orgFriendlyName}/admin/work-allocation/programs/${programId}/program-evaluation`,
        programPermissions: (orgFriendlyName: string, programId: string) =>
          `/app/${orgFriendlyName}/admin/work-allocation/programs/${programId}/program-permissions`,
        programEmails: (orgFriendlyName: string, programId: string) =>
          `/app/${orgFriendlyName}/admin/work-allocation/programs/${programId}/program-emails`,
      },
      groups: {
        view: (orgFriendlyName: string, programId: string, groupId: string) =>
          `/app/${orgFriendlyName}/admin/work-allocation/programs/${programId}/groups/${groupId}`,
      },
      edit: {
        programDetails: (orgFriendlyName: string, programId: number) =>
          `/app/${orgFriendlyName}/admin/work-allocation/programs/create?programId=${programId}`,
        programEvaluation: (orgFriendlyName: string, programId: number) =>
          `/app/${orgFriendlyName}/admin/work-allocation/programs/${programId}/edit-program-evaluation`, // Unused
      },
    },
  },
  candidateReview: {
    details: (orgFriendlyName: string, reviewId: string) =>
      `/app/${orgFriendlyName}/admin/candidate-review/${reviewId}/details`,
    adminCandidateReview: (orgFriendlyName: string) =>
      `/app/${orgFriendlyName}/admin/candidate-review`,

    adminCandidateList: (
      orgFriendlyName: string,
      reviewId: string,
      sort?: URL_SORT_CONFIG,
    ) => {
      const baseURL = `/app/${orgFriendlyName}/admin/candidate-review/${reviewId}/details`;
      return addSortToBaseURL(baseURL, sort);
    },

    memberCandidateList: (
      orgFriendlyName: string,
      reviewId: string,
      sort?: URL_SORT_CONFIG,
    ) => {
      const baseURL = `/app/${orgFriendlyName}/candidate-review/${reviewId}`;
      return addSortToBaseURL(baseURL, sort);
    },

    view: (
      orgFriendlyName: string,
      reviewId: string,
      candidateId: string,
      sort?: URL_SORT_CONFIG,
      jobAppId?: string,
    ) => {
      const baseURL = `app/${orgFriendlyName}/candidate-review/${reviewId}/${candidateId}`;

      if (jobAppId) {
        const queryParams = objectToQueryParams(
          {
            jobAppId,
          },
          "&",
        );
        return addSortToBaseURL(baseURL, sort) + queryParams;
      }
      return addSortToBaseURL(baseURL, sort);
    },
  },
  /*────────────────────────────*\
     │           Forward          │
    \*────────────────────────────*/
  forward: {
    homePage: () => "/app/forward",
    settings: {
      profile: () => "/app/forward/settings",
      emails: () => "/app/forward/settings/emails",
      personalInfo: () => "/app/forward/settings/personal-info",
      preferences: () => "/app/forward/settings/preferences",
      privacyAndSharing: () => "/app/forward/settings/privacy-and-sharing",
    },
    eventSchedulePage: () => `/app/forward`,
    jobActivity: () => `/app/forward/job-activity`,
    discovery: {
      jobBoard: () => `/app/forward/jobs`,
      firmDirectory: () => `/app/forward/firms`,
      jobDetail: (jobId: string) => `/app/forward/jobs/${jobId}`,
      jobDetailNotFound: () => `/app/forward/jobs/not-found`,
      firmProfile: (orgFriendlyName: string) =>
        `/app/forward/firms/${orgFriendlyName}`,
      firmProfileIframeUrl: (orgFriendlyName: string) =>
        `https://start.florecruit.com/forward/firm/${orgFriendlyName}`,
    },
    events: {
      directory: () => `/app/forward/events`,
      eventDetail: (eventId: string) => `/app/forward/events/${eventId}`,
    },
    signIn: {
      entry: () => "/app/forward/sign-in",
      // Not a page in our app
      orgLogin: () => "https://florecruit.com/signin",
      emailSent: () => "/app/forward/sign-in/email-sent/",
      accountNotFound: () => "/app/forward/account-not-found",
    },
    signUp: {
      entry: (
        wasUnknownEmail: boolean,
        previousEmail: string | null,
        encodedRedirectUrl?: string | null,
      ) =>
        `/app/forward/sign-up?unknown-email=${wasUnknownEmail}` +
        (previousEmail
          ? `&previous-email=${encodeURIComponent(previousEmail)}`
          : "") +
        (encodedRedirectUrl ? `&redirect=${encodedRedirectUrl}` : ""),
      emailSent: (encodedRedirectUrl?: string | null) =>
        "/app/forward/sign-up/email-sent/" +
        (encodedRedirectUrl ? `?redirect=${encodedRedirectUrl}` : ""),
      aboutYourself: (encodedRedirectUrl?: string | null) =>
        "/app/forward/sign-up/about-yourself" +
        (encodedRedirectUrl
          ? `?redirect=${encodeURIComponent(encodedRedirectUrl)}`
          : "/"),
      lawDegree: (encodedRedirectUrl?: string | null) =>
        "/app/forward/sign-up/law-degree" +
        (encodedRedirectUrl
          ? `?redirect=${encodeURIComponent(encodedRedirectUrl)}`
          : "/"),
      preferredOfficeLocations: (encodedRedirectUrl?: string | null) =>
        "/app/forward/sign-up/preferred-office-locations" +
        (encodedRedirectUrl
          ? `?redirect=${encodeURIComponent(encodedRedirectUrl)}`
          : "/"),
      preferredPracticeCategories: (encodedRedirectUrl?: string | null) =>
        // externally we call them practice "areas"
        "/app/forward/sign-up/preferred-practice-areas" +
        (encodedRedirectUrl
          ? `?redirect=${encodeURIComponent(encodedRedirectUrl)}`
          : "/"),
      headshot: (encodedRedirectUrl?: string | null) =>
        "/app/forward/sign-up/headshot" +
        (encodedRedirectUrl
          ? `?redirect=${encodeURIComponent(encodedRedirectUrl)}`
          : "/"),
    },
    accountActivation: {
      general: () => "/app/forward/activate-account/",
      // TODO: Implement on BE
      redirectGeneral: () => `/api/v2/forward/account/activation/`,
      redirectFromEventRegistration: (
        orgFriendlyName: string,
        eventId: string,
        recruitAttendeeExtension: string,
      ) =>
        `/api/v2/forward/account/activation/${orgFriendlyName}/${eventId}/${recruitAttendeeExtension}`,
    },
  },
  member: {
    workAllocation: {
      programs: {
        list: (org: string) => `/app/${org}/member/work-allocation/programs`,
        view: (org: string, programId: string) =>
          `/app/${org}/member/work-allocation/programs/${programId}`,
        matters: {
          create: (org: string, programId: string) =>
            `/app/${org}/member/work-allocation/programs/${programId}/create-matter`,
          view: (org: string, programId: string, matterId: string) =>
            `/app/${org}/member/work-allocation/programs/${programId}/matters/${matterId}`,
          edit: (org: string, programId: string, matterId: string) =>
            `/app/${org}/member/work-allocation/programs/${programId}/create-matter?matterId=${matterId}`,
        },
        groups: {
          view: (org: string, programId: string, groupId: string) =>
            `/app/${org}/member/work-allocation/programs/${programId}/groups/${groupId}`,
        },
      },
    },
  },
  performance: {
    admin: {
      reviewCycles: {
        list: (orgFriendlyName: string) =>
          `/app/${orgFriendlyName}/performance/admin/review-cycles`,
        view: (orgFriendlyName: string, reviewCycleId: string) =>
          `/app/${orgFriendlyName}/performance/admin/review-cycles/${reviewCycleId}`,
        edit: (orgFriendlyName: string, reviewCycleId: string) =>
          `/app/${orgFriendlyName}/performance/admin/review-cycles/${reviewCycleId}/edit`,
      },
      settings: {
        evaluations: {
          forms: (orgFriendlyName: string) =>
            `/app/${orgFriendlyName}/performance/admin/settings/evaluations/forms`,
        },
      },
    },
    employee: {
      reviewCycles: {
        list: (orgFriendlyName: string) =>
          `/app/${orgFriendlyName}/performance/review-cycles`,
        view: (orgFriendlyName: string, reviewCycleId: string) =>
          `/app/${orgFriendlyName}/performance/review-cycles/${reviewCycleId}`,
        evaluations: {
          edit: (
            orgFriendlyName: string,
            reviewCycleId: string,
            evaluationId: string,
          ) =>
            `/app/${orgFriendlyName}/performance/review-cycles/${reviewCycleId}/evaluations/${evaluationId}/edit`,
        },
        requests: {
          incoming: (orgFriendlyName: string) =>
            `/app/${orgFriendlyName}/performance/review-cycles/requests/incoming`,
          outgoing: (orgFriendlyName: string) =>
            `/app/${orgFriendlyName}/performance/review-cycles/requests/outgoing`,
        },
      },
    },
  },
};

// returns the full qualified URL for the given path
// e.g. useFullURL('/app/forward') => 'https://florecruit.com/app/forward'
export function useAbsoluteUrl(path: string): string {
  const router = useRouter();
  return window.location.origin + router.basePath + path;
}
